import type { LucideIcon } from 'lucide-react';
import { Filter, Layers, MousePointerClick, Wrench } from 'lucide-react';

import type { BatchModeFilter } from '@/hooks/useWorkflowBatchJobs';

/**
 * How the batch was selected, as a filter and as a badge.
 *
 * The tones say what kind of thing the reader is looking at rather than how it
 * went — status already carries that. Neutral for a human picking traces, blue
 * for a query, amber for an engine-driven run, which is the one a business user
 * should not mistake for their own work.
 */
export const MODE_FILTERS = [
  {
    key: 'all',
    label: 'All types',
    icon: Layers,
    tone: 'bg-foreground/5 text-foreground/60'
  },
  {
    key: 'explicit',
    label: 'Manually selected',
    icon: MousePointerClick,
    tone: 'bg-foreground/5 text-foreground/70'
  },
  {
    key: 'filter',
    label: 'Via filters',
    icon: Filter,
    tone: 'bg-blue-500/10 text-blue-700 dark:text-blue-400'
  },
  {
    key: 'migration',
    label: 'Trace migration',
    icon: Wrench,
    tone: 'bg-amber-500/10 text-amber-700 dark:text-amber-500'
  }
] as const satisfies readonly {
  key: BatchModeFilter;
  label: string;
  icon: LucideIcon;
  tone: string;
}[];

export function isBatchModeFilter(value: string): value is BatchModeFilter {
  return MODE_FILTERS.some(filter => filter.key === value);
}

const TONE_BY_MODE: Record<string, string> = Object.fromEntries(
  MODE_FILTERS.filter(f => f.key !== 'all').map(f => [f.key, f.tone])
);

/** Badge classes for a job's mode. Falls back to neutral for a mode we do not know. */
export function modeTone(mode: string): string {
  return TONE_BY_MODE[mode] ?? 'bg-foreground/5 text-foreground/60';
}
